import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { PlayerComponent } from '@pool-league/frontend-shared-ui-player';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  startWith,
  Subject,
  withLatestFrom,
} from 'rxjs';
import { Player, playersFeature } from '@pool-league/frontend-web-data-players';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-web-player-picker',
  imports: [PlayerComponent],
  templateUrl: './player-picker.component.html',
  styleUrl: './player-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlayerPickerComponent {
  @ViewChild('filterInput', { read: ElementRef })
  filterInput!: ElementRef<HTMLInputElement>;
  @ViewChild('resultsList', { read: ElementRef })
  resultsList!: ElementRef<HTMLUListElement>;

  readonly excludeRetired = input<boolean>(true);
  readonly excludeIds = input<string[]>([]);
  readonly label = input<string>('Select player');
  readonly focusIndex = signal(0);

  readonly selectPlayer = output<string>();

  readonly #store = inject(Store);
  readonly #availablePlayers = combineLatest([
    this.#store.select(playersFeature.selectAll),
    toObservable(this.excludeIds),
    toObservable(this.excludeRetired),
  ]).pipe(
    map(([players, exclude, excludeRetired]) =>
      players.filter(
        (p) => !exclude.includes(p.id) && (!excludeRetired || !p.retired),
      ),
    ),
  );

  readonly #filter = new Subject<string>();
  readonly #reset = new Subject<void>();

  readonly #players = merge(
    this.#filter.pipe(debounceTime(400)),
    this.#reset.pipe(map(() => '')),
  ).pipe(
    map((term) => term.trim().toLowerCase()),
    distinctUntilChanged(),
    withLatestFrom(this.#availablePlayers),
    map(([term, players]) =>
      term
        ? players.filter((p) => p.displayName.toLowerCase().includes(term))
        : [],
    ),
    startWith<Player[]>([]),
  );

  readonly players = toSignal(this.#players, { requireSync: true });

  clickOption(playerId: string) {
    this.filterInput.nativeElement.value = '';
    this.filterInput.nativeElement.focus();
    this.#reset.next();
    this.selectPlayer.emit(playerId);
  }

  filter() {
    this.focusIndex.set(0);
    this.#filter.next(this.filterInput.nativeElement.value);
  }

  filterInputKeyDown(ev: KeyboardEvent) {
    if (!this.resultsList) {
      return;
    }
    switch (ev.key) {
      case 'ArrowDown':
        this.focusIndex.set(0);
        (
          this.resultsList.nativeElement.firstElementChild
            ?.firstElementChild as HTMLButtonElement
        )?.focus();
        break;
      case 'ArrowUp':
        this.focusIndex.set(
          this.resultsList.nativeElement.childElementCount - 1,
        );
        (
          this.resultsList.nativeElement.lastElementChild
            ?.firstElementChild as HTMLButtonElement
        )?.focus();
        break;
    }
  }

  optionKeyDown(ev: KeyboardEvent, index: number) {
    const target = ev.currentTarget as HTMLButtonElement;
    if (target.parentElement?.parentElement?.children.length === 1) {
      switch (ev.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          this.filterInput.nativeElement.focus();
          break;
      }
      return;
    }

    switch (ev.key) {
      case 'ArrowDown':
        if (target.parentElement?.nextElementSibling) {
          this.focusIndex.set(index + 1);
          (
            target.parentElement.nextElementSibling
              .firstElementChild as HTMLButtonElement
          ).focus();
        } else {
          this.filterInput.nativeElement.focus();
        }
        break;
      case 'ArrowUp':
        if (target.parentElement?.previousElementSibling) {
          this.focusIndex.set(index - 1);
          (
            target.parentElement.previousElementSibling
              .firstElementChild as HTMLButtonElement
          ).focus();
        } else {
          this.filterInput.nativeElement.focus();
        }
        break;
    }
  }
}
